import { createClient } from '@connectrpc/connect';
import { FamilyService } from './gen/family_service_pb';
import { pollRevitOperation, type RevitOperationProgressSnapshot } from './operationProgressClient';
import { getGrpcTransport } from './transport';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { parseAuthoritativeModelVersion } from './requestedParameterIntent';

export interface QuickFillApplyBatchResult {
  success: boolean;
  totalUpdates?: number;
  successCount?: number;
  errorCount?: number;
  errors?: string[];
  error?: string;
}

export interface QuickFillApplyResult {
  success: boolean;
  familyTypeResult?: QuickFillApplyBatchResult;
  elementResult?: QuickFillApplyBatchResult;
  modelVersion?: number;
  error?: string;
}

export interface QuickFillTypeUpdate {
  familyType: string;
  parameterName: string;
  value: string;
  familyTypeId?: string;
  targetElementIds?: string[];
  bindingKind?: 'type' | 'instance';
}

export interface QuickFillElementUpdate {
  elementId: string;
  parameterName: string;
  value: string;
}

function mapBatchResult(result: any): QuickFillApplyBatchResult | undefined {
  if (!result) return undefined;
  return {
    success: Boolean(result.isSuccess ?? result.success),
    totalUpdates: Number(result.totalUpdates) || undefined,
    successCount: Number(result.successCount) || undefined,
    errorCount: Number(result.errorCount) || undefined,
    errors: Array.isArray(result.errors) && result.errors.length > 0 ? result.errors : undefined,
    error: result.error || undefined,
  };
}

function parseQuickFillApplyResult(resultJson: string): QuickFillApplyResult {
  const parsed = JSON.parse(resultJson);
  const familyTypeResult = mapBatchResult(parsed.familyTypeResult);
  const elementResult = mapBatchResult(parsed.elementResult);
  return {
    success: (familyTypeResult?.success ?? true) && (elementResult?.success ?? true),
    familyTypeResult,
    elementResult,
    modelVersion: parseAuthoritativeModelVersion(parsed.modelVersion),
    error: [familyTypeResult?.error, elementResult?.error].filter(Boolean).join('; ') || undefined,
  };
}

export const __quickFillApplyClientTestInternals = { parseQuickFillApplyResult };

export async function startQuickFillApplyGrpc(
  familyTypeUpdates: QuickFillTypeUpdate[],
  elementUpdates: QuickFillElementUpdate[],
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<QuickFillApplyResult> {
  const transport = getGrpcTransport();
  if (!transport) {
    return { success: false, error: 'gRPC transport is unavailable' };
  }
  const client = createClient(FamilyService, transport);
  try {
    const start = await client.startQuickFillApply({ familyTypeUpdates, elementUpdates });
    if (!start.isSuccess || !start.operationId) {
      return { success: false, error: start.error || 'Quick Fill apply operation did not start' };
    }
    const operation = await pollRevitOperation(start.operationId, {
      onProgress: options?.onProgress,
      parseResult: parseQuickFillApplyResult,
    });
    if (!operation.success || !operation.result) {
      return { success: false, error: operation.error || 'Quick Fill apply operation failed' };
    }
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(
      operation.result.success ? null : `StartQuickFillApply: ${operation.result.error || 'apply failed'}`
    );
    return operation.result;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'StartQuickFillApply gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`StartQuickFillApply: ${message}`);
    return { success: false, error: message };
  }
}
